import React, { useState } from 'react';
import { useAuth } from '../store/AuthContext';

export function Login() {
  const { login } = useAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      if (!err.response) {
        setError('Cannot connect to server. Check your internet connection or contact the administrator.');
      } else {
        setError(err.response.data?.message || 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (role) => {
    const creds = {
      admin:    ['admin@empowertruck.com', 'Password123!'],
      fleet:    ['fleet@empowertruck.com', 'Password123!'],
      dispatch: ['dispatch@empowertruck.com', 'Password123!'],
      driver:   ['driver1@empowertruck.com', 'Password123!'],
    };
    const [e, p] = creds[role];
    setEmail(e); setPassword(p);
    setError(''); setLoading(true);
    try {
      await login(e, p);
    } catch (err) {
      if (!err.response) {
        setError('Cannot connect to server. Make sure XAMPP is running.');
      } else {
        setError(err.response.data?.message || 'Login failed.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-8 py-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur">
              <svg className="w-9 h-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M8 7h12m0 0l-4-4m4 4l-4 4M4 17l4 4m-4 0l4-4" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white">TruckManager</h1>
            <p className="text-blue-200 text-sm mt-1">Empower Truck Sales — Fleet Operations</p>
          </div>

          {/* Form */}
          <div className="px-8 py-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Sign In to Your Account</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  className="input-field"
                  placeholder="you@empowertruck.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0">Password</label>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className="input-field pr-10"
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPass ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 text-base"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in…
                  </span>
                ) : 'Sign In'}
              </button>
            </form>

            {/* Quick login for demo */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center mb-3 font-medium uppercase tracking-wide">Quick Demo Access</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { role: 'admin',    label: 'Administrator', color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100' },
                  { role: 'fleet',    label: 'Fleet Manager',  color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' },
                  { role: 'dispatch', label: 'Dispatcher',     color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
                  { role: 'driver',   label: 'Driver',         color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
                ].map(q => (
                  <button
                    key={q.role}
                    type="button"
                    onClick={() => quickLogin(q.role)}
                    disabled={loading}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${q.color} disabled:opacity-50`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-xs mt-4">
          Contact your fleet manager for access credentials.
        </p>
      </div>
    </div>
  );
}
